import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tag } from "@/components/ui/tag";
import { refreshTokensApi, type RefreshToken } from "@/api/refreshTokensApi";

interface DevicesSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DevicesSheet = ({ open, onOpenChange }: DevicesSheetProps) => {
  const [devices, setDevices] = useState<RefreshToken[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState<number | "all" | null>(null);
  const [isLogoutAllConfirmOpen, setIsLogoutAllConfirmOpen] = useState(false);

  const fetchDevices = async () => {
    try {
      const tokens = await refreshTokensApi.getAll();
      setDevices(tokens);
    } catch (error) {
      console.error("Failed to fetch devices:", error);
      toast.error("Не удалось загрузить список устройств");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      fetchDevices();
    }
  }, [open]);

  const handleLogoutDevice = async (deviceId: number) => {
    setIsDeleting(deviceId);
    try {
      await refreshTokensApi.delete(deviceId);
      toast.success("Устройство успешно отключено");
      setDevices((prev) => prev.filter((d) => d.id !== deviceId));
    } catch (error) {
      console.error("Failed to logout device:", error);
      toast.error("Не удалось отключить устройство");
    } finally {
      setIsDeleting(null);
    }
  };

  const handleLogoutAll = () => {
    setIsLogoutAllConfirmOpen(true);
  };

  const handleConfirmLogoutAll = async () => {
    setIsLogoutAllConfirmOpen(false);
    setIsDeleting("all");
    try {
      await refreshTokensApi.deactivateAll();
      toast.success("Все сессии успешно остановлены");
      // Refetch to show only current device
      await fetchDevices();
    } catch (error) {
      console.error("Failed to logout all devices:", error);
      toast.error("Не удалось остановить все сессии");
    } finally {
      setIsDeleting(null);
    }
  };

  const cleanName = (name?: string | null) => (name ?? "").replace(/"/g, "").trim();

  const splitDeviceName = (name?: string | null) => {
    const cleaned = cleanName(name);
    if (!cleaned) return { title: "Unknown", subtitle: "" };

    const parts = cleaned.split(/\s+/);
    return {
      title: parts[0] ?? "Unknown",
      subtitle: parts.slice(1).join(" "),
    };
  };

  const formatLastOnline = (dateStr?: string | null) => {
    if (!dateStr) return "неизвестно";

    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "только что";
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} д назад`;
    return date.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
  };

  const isActive = (dateStr?: string | null) => {
    if (!dateStr) return false;
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    return diffMins < 5; // Активен если меньше 5 минут
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="flex h-dvh w-145 max-w-full flex-col border-0 bg-neutral-900 px-24.5 py-10 sm:max-w-145"
      >
        <SheetHeader className="mb-12 p-0">
          <SheetTitle className="fs-600 font-bold text-white">Сессии на устройствах</SheetTitle>
        </SheetHeader>

        <div className="flex flex-1 flex-col gap-12 overflow-y-auto scrollbar-hidden">
          {isLoading ? (
            <div className="fs-300 py-8 text-center text-neutral-400">Загрузка...</div>
          ) : devices.length === 0 ? (
            <div className="fs-300 py-8 text-center text-neutral-400">
              Нет активных устройств
            </div>
          ) : (
            <>
              {/* Sessions Container */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4">
                  {devices.map((device) => {
                    const { title, subtitle } = splitDeviceName(device.device_name);
                    const lastOnline = device.last_online_at ?? device.created_at;
                    const onlineRecently = isActive(lastOnline);

                    return (
                      <div
                        key={device.id}
                        className="flex items-center justify-between border-b border-neutral-200/7 pb-4"
                      >
                        <div className="flex flex-col gap-2">
                          <div className="fs-300 font-medium text-white">
                            {title} {device.device_type || "Unknown"}
                          </div>
                          <div className="fs-200 text-neutral-200/60">
                            {subtitle || "Unknown"}
                            <br />
                            Онлайн: {formatLastOnline(lastOnline)}
                          </div>
                        </div>
                    
                        <div className="flex flex-col items-end gap-2">
                          <Tag
                            variant={
                              !device.is_active ? "negative" : onlineRecently ? "positive" : "neutral"
                            }
                          >
                            {!device.is_active ? "Остановлена" : onlineRecently ? "Активен" : "Не в сети"}
                          </Tag>
                          
                          {device.is_active && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleLogoutDevice(device.id)}
                              disabled={isDeleting === device.id || isDeleting === "all"}
                            >
                              Стоп
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Stop All Sessions Button */}
        {!isLoading && devices.length > 0 && (
          <div className="shrink-0 pt-4">
            <Button
              variant="destructive"
              size="sm"
              className="w-full"
              onClick={handleLogoutAll}
              disabled={isDeleting !== null}
            >
              Остановить все сессии
            </Button>
          </div>
        )}
      </SheetContent>

      <ConfirmDialog
        open={isLogoutAllConfirmOpen}
        onOpenChange={setIsLogoutAllConfirmOpen}
        title="Остановить все сессии?"
        description="Все сессии, кроме текущей, будут завершены. Подтвердите действие."
        isLoading={isDeleting === "all"}
        cancelButton={{ children: "Отмена", variant: "neutral" }}
        confirmButton={{
          children: "Остановить",
          variant: "destructive",
          onClick: handleConfirmLogoutAll,
        }}
      />
    </Sheet>
  );
};
